import { useCallback, useState } from 'react';

import { type IRowNode } from '@mint-lib/ag-grid/community';
import {
  type GetContextMenuItems,
  type MenuItemDef,
} from '@mint-lib/ag-grid/enterprise';
import { trackAction } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import {
  CheckmarkOutline,
  Delete,
  Edit,
  Error as ErrorIcon,
  SendAlt,
} from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useProfile } from '@mint-lib/profile-helpers';
import { Auth } from '@mint-sdk/api-auth';

import { USER_STATUS } from '../../../constants/userTableV2.js';
import MenuItem from '../components/MenuItem.jsx';
import type { UserTableV2Row } from '../datasource.js';
import type { SetUserUpdating } from '../gridContext.js';

type Options = {
  isReadonlyPermission: boolean;
  onEditClick: (user: UserTableV2Row) => void;
  onUsersChange: () => void;
  setUserUpdating: SetUserUpdating;
};

const getUserStatus = (user: UserTableV2Row) => {
  if (user.status === USER_STATUS.Active) return USER_STATUS.Active;
  if (
    user.status === USER_STATUS.Pending ||
    user.status === USER_STATUS.PendingExistingUser ||
    user.status === USER_STATUS.PendingNewUser ||
    user.status === USER_STATUS.Expired
  )
    return USER_STATUS.Pending;
  return USER_STATUS.Deactivated;
};

const useUserRowActions = ({
  isReadonlyPermission,
  onEditClick,
  onUsersChange,
  setUserUpdating,
}: Options) => {
  const { t } = useTranslation('@myn/permissions');
  const { profile } = useProfile();

  const deleteUser = Auth.deleteUserV2();
  const resendUserInvitation = Auth.resendUserInvitationV2();
  const toggleStatus = Auth.switchUserStatusV2();
  const editUser = Auth.editUserV2();

  const [deleteTarget, setDeleteTarget] = useState<UserTableV2Row | null>(null);

  const handleResendInvitation = useCallback(
    (user: UserTableV2Row, node: IRowNode<UserTableV2Row>) => {
      resendUserInvitation.mutate(
        { data: { id: user.id } },
        {
          onSuccess: (data) => {
            trackAction('User management: resend user invitation');
            enqueueSnackbar({
              removable: true,
              variant: 'success',
              title: t('Success'),
              textLayout: 'multiline',
              subTitle: t('Invitation sent to user.'),
            });
            node.setData(data);
          },
          onError: () => {
            enqueueSnackbar({
              removable: true,
              variant: 'error',
              title: t('Failure'),
              textLayout: 'multiline',
              subTitle: t('Something went wrong. Please try again'),
            });
          },
        },
      );
    },
    [resendUserInvitation, t],
  );

  const handleRoleChange = useCallback(
    (
      user: UserTableV2Row,
      newRole: string | number,
      node: IRowNode<UserTableV2Row>,
    ) => {
      setUserUpdating(user.id, true);
      editUser.mutate(
        { urlParams: { id: user.id }, data: { role: newRole } },
        {
          onSuccess: (data) => {
            trackAction('User management: change user role');
            node.setData(data);
            enqueueSnackbar({
              removable: true,
              variant: 'success',
              title: t('Success'),
              textLayout: 'multiline',
              subTitle: t('User role updated.'),
            });
          },
          onError: () => {
            enqueueSnackbar({
              removable: true,
              variant: 'error',
              title: t('Failure'),
              textLayout: 'multiline',
              subTitle: t('Something went wrong. Please try again'),
            });
          },
          onSettled: () => setUserUpdating(user.id, false),
        },
      );
    },
    [editUser, setUserUpdating, t],
  );

  const handleToggleActivity = useCallback(
    (user: UserTableV2Row, node: IRowNode<UserTableV2Row>) => {
      const isDeactivating = getUserStatus(user) === USER_STATUS.Active;
      setUserUpdating(user.id, true);

      toggleStatus.mutate(
        { data: { id: user.id } },
        {
          onSuccess: (data) => {
            trackAction('User management: toggle user status', {
              isActive: !isDeactivating,
            });
            node.setData(data);
            enqueueSnackbar({
              removable: true,
              variant: 'success',
              title: t('Success'),
              textLayout: 'multiline',
              subTitle: t(
                isDeactivating ? 'User deactivated.' : 'User activated.',
              ),
            });
          },
          onError: () => {
            enqueueSnackbar({
              removable: true,
              variant: 'error',
              title: t('Failure'),
              textLayout: 'multiline',
              subTitle: t('Something went wrong. Please try again'),
            });
          },
          onSettled: () => setUserUpdating(user.id, false),
        },
      );
    },
    [setUserUpdating, t, toggleStatus],
  );

  const getContextMenuItems = useCallback<GetContextMenuItems<UserTableV2Row>>(
    (params) => {
      if (isReadonlyPermission) return [];

      const node = params.node;
      const user = node?.data;
      if (!user || !node) return [];

      const isSelf = profile?.id === user.id;
      const isDeactivatedSystem = user.status === USER_STATUS.DeactivatedSystem;
      const status = getUserStatus(user);
      const isActive = status === USER_STATUS.Active;

      const editItem: MenuItemDef = {
        name: t('Edit'),
        disabled: isReadonlyPermission,
        menuItem: MenuItem,
        menuItemParams: { IconComponent: Edit, color: 'primary' },
        action: () => onEditClick(user),
      };

      const statusItem: MenuItemDef =
        status === USER_STATUS.Pending
          ? {
              name: t('Resend invitation'),
              disabled: isReadonlyPermission,
              menuItem: MenuItem,
              menuItemParams: { IconComponent: SendAlt, color: 'primary' },
              action: () => handleResendInvitation(user, node),
            }
          : {
              name: isActive ? t('Deactivate') : t('Activate'),
              disabled: isReadonlyPermission,
              menuItem: MenuItem,
              menuItemParams: {
                IconComponent: isActive ? ErrorIcon : CheckmarkOutline,
                color: 'primary',
              },
              action: () => handleToggleActivity(user, node),
            };

      const removeItem: MenuItemDef = {
        name: t('Remove'),
        disabled: isReadonlyPermission,
        menuItem: MenuItem,
        menuItemParams: {
          IconComponent: Delete,
          color: isDeactivatedSystem ? 'error' : 'warning',
        },
        action: () => setDeleteTarget(user),
      };

      // A deactivated-system user can only be removed, and you can only edit yourself.
      if (isDeactivatedSystem) return [removeItem];
      if (isSelf) return [editItem];

      return [editItem, statusItem, 'separator', removeItem];
    },
    [
      handleResendInvitation,
      handleToggleActivity,
      isReadonlyPermission,
      onEditClick,
      profile?.id,
      t,
    ],
  );

  return {
    getContextMenuItems,
    deleteTarget,
    setDeleteTarget,
    deleteUser,
    handleRoleChange,
  };
};

export default useUserRowActions;
